import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useTestimonials } from "@/lib/useTestimonials";

function Stars({ n, size = 14 }) {
    return (
        <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={size} className={i <= n ? "fill-primary text-primary" : "text-border"} />
            ))}
        </span>
    );
}

export default function GoogleReviews() {
    const items = useTestimonials();
    if (items.length === 0) return null;

    const avg = (items.reduce((s, r) => s + r.rating, 0) / items.length).toFixed(1);

    return (
        <div className="flex flex-col gap-5" data-testid="testimonials-section" id="testimonials">
            <div className="flex items-end justify-between gap-4">
                <h2 className="font-display text-2xl font-bold text-primary sm:text-3xl" data-testid="reviews-heading">
                    What contractors say about PayMe
                </h2>
                <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3" data-testid="testimonials-summary">
                    <span className="font-mono text-3xl font-bold text-primary">{avg}</span>
                    <div>
                        <Stars n={Math.round(Number(avg))} />
                        <p className="mt-1 text-xs text-muted-foreground">from {items.length} Google review{items.length === 1 ? "" : "s"}</p>
                    </div>
                </div>
            </div>

            {items.map((r, i) => (
                <motion.figure
                    key={r.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="group relative rounded-2xl border border-border bg-surface p-6 transition-colors duration-300 hover:border-primary/50"
                    data-testid={`testimonial-card-${i}`}
                >
                    <Quote size={24} className="absolute right-5 top-5 text-primary/15 transition-colors duration-300 group-hover:text-primary/30" />
                    <Stars n={r.rating} />
                    <blockquote className="mt-3 text-base leading-relaxed text-foreground/90">“{r.text}”</blockquote>
                    <figcaption className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
                                {r.author.charAt(0)}
                            </span>
                            <div>
                                <p className="text-sm font-semibold">{r.author}</p>
                                {r.review_date && <p className="text-xs text-muted-foreground">{r.review_date}</p>}
                            </div>
                        </div>
                        <span className="rounded-full border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            via Google
                        </span>
                    </figcaption>
                </motion.figure>
            ))}
        </div>
    );
}
